import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  rainfallGullyTaggingAtom,
  rainfallPlaybackPlayingAtom,
  rainfallPlaybackSpeedAtom,
  rainfallPlaybackTimeAtom,
  rainfallStormScaleAtom,
} from '@/atoms/rainfall.atom';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { execute } from '@/graphql/execute';
import { scaledFillTimeS, scaledStormIntensity } from '@/lib/rainfallScaling';
import { isPondDrained } from '@/lib/rainfallStyling';
import { featuresByLayerQuery } from '@/queries/feature.query';

import { fmtDurationS } from './RainfallFeatureView';

interface Props {
  /** Total storm duration in seconds. Used as the slider's upper bound
   *  and as the auto-pause point. */
  stormDurationS: number;
  /** Storm intensity (mm/h) for the inline readout next to the timestamp.
   *  Optional: TOPOLOGY-mode runs that somehow reach this component
   *  shouldn't be a hard error. */
  intensityMmPerHr?: number;
  /** Name of the rainfall layer the playback drives. Surfaced as a
   *  prefix label only when more than one STORM layer is visible at
   *  once — otherwise the layer is unambiguous and the label adds
   *  noise. */
  layerName?: string;
  /** Layer id used to read pond features for the annotated overflow
   *  markers along the slider. Same `['features', layerId]` cache that
   *  Viewer.tsx populates at mount. */
  layerId?: string;
}

const SPEED_OPTIONS = [1, 2, 5, 10] as const;

/**
 * Bottom-of-viewer playback bar for animated storm fill. Mounts only
 * when at least one rainfall STORM-mode layer is visible (the parent
 * does that gating).
 *
 * Controls (left → right): play/pause, skip-to-start, time slider,
 * "current / total" timestamp, speed selector.
 *
 * The RAF loop only runs while playing — paused state holds the slider
 * at whatever time the user last scrubbed to. Auto-pauses at the end
 * so users don't have to chase the thumb.
 *
 * Hotkeys (when the bar is visible):
 *   - Spacebar  → play / pause
 *   - ArrowLeft / ArrowRight → scrub ±5% of stormDurationS
 *   - Home → jump to t=0
 *
 * Listener is attached at window-level but ignores events whose target
 * is inside an editable element so we don't hijack the user's typing.
 */
export function RainfallPlaybackBar({
  stormDurationS,
  intensityMmPerHr,
  layerName,
  layerId,
}: Props) {
  const { t } = useTranslation('project');
  const [time, setTime] = useAtom(rainfallPlaybackTimeAtom);
  const [playing, setPlaying] = useAtom(rainfallPlaybackPlayingAtom);
  const [speed, setSpeed] = useAtom(rainfallPlaybackSpeedAtom);
  const stormScale = useAtomValue(rainfallStormScaleAtom);
  const isScaled = Math.abs(stormScale - 1) > 1e-6;
  const gullies = useAtomValue(rainfallGullyTaggingAtom);
  const scaledIntensity = scaledStormIntensity(intensityMmPerHr ?? null, stormScale);

  // Keep `time` and `playing` in refs so the RAF callback doesn't have
  // to re-bind on every state change — the loop reads the latest
  // values without retriggering the effect on every tick.
  const timeRef = useRef(time);
  const playingRef = useRef(playing);
  const speedRef = useRef(speed);
  timeRef.current = time;
  playingRef.current = playing;
  speedRef.current = speed;

  // Annotated overflow markers — Feature 2. Read pond features from
  // the same `['features', layerId]` query the leaderboard reads,
  // dedupe via React-Query.
  const featuresQuery = useQuery({
    queryKey: ['features', layerId ?? '__none__'],
    queryFn: () =>
      layerId
        ? execute(featuresByLayerQuery, { layerIds: [layerId] })
        : Promise.resolve({ featuresByLayer: [] as Array<unknown> } as never),
    staleTime: Infinity,
    enabled: layerId != null,
  });
  const markers = useMarkerEvents(
    featuresQuery.data?.featuresByLayer,
    gullies,
    stormScale,
    stormDurationS,
  );

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let lastTs = performance.now();
    const tick = (ts: number) => {
      const dt = (ts - lastTs) / 1000;
      lastTs = ts;
      const next = Math.min(stormDurationS, timeRef.current + dt * speedRef.current);
      timeRef.current = next;
      setTime(next);
      if (next >= stormDurationS) {
        setPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, stormDurationS, setTime, setPlaying]);

  // Reset the clock back to 0 when the underlying storm changes
  // (different layer selected, or new run produced a different storm
  // duration). Without this, switching between two STORM layers would
  // leave the slider at the previous run's elapsed time.
  useEffect(() => {
    if (timeRef.current > stormDurationS) {
      setTime(0);
    }
  }, [stormDurationS, setTime]);

  // Keyboard hotkeys — bound at window-level but skipped when the user
  // is typing in an editable element. Standard video-player feel.
  useEffect(() => {
    const handler = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      if (ev.key === ' ' || ev.code === 'Space') {
        ev.preventDefault();
        if (!playingRef.current && timeRef.current >= stormDurationS) setTime(0);
        setPlaying(!playingRef.current);
      } else if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        const step = Math.max(1, stormDurationS * 0.05);
        const next = Math.min(stormDurationS, timeRef.current + step);
        setTime(next);
      } else if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        const step = Math.max(1, stormDurationS * 0.05);
        const next = Math.max(0, timeRef.current - step);
        setTime(next);
        if (playingRef.current) setPlaying(false);
      } else if (ev.key === 'Home') {
        ev.preventDefault();
        setTime(0);
        setPlaying(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [stormDurationS, setTime, setPlaying]);

  return (
    <div
      // Fill all available width inside the wrapper (which already
      // reserves space for the rainfall panel + the layers panel
      // alongside the viewer). Capped at 720 px so on a wide screen
      // the bar doesn't sprawl. Switching from `width: 60%` —
      // percentage of the wrapper made the bar invisibly narrow when
      // the layers panel was open + viewport was narrow.
      className='pointer-events-auto flex w-full max-w-[720px] items-center gap-2 rounded-md border border-slate-200/70 bg-white/90 px-3 py-1.5 text-xs shadow-sm backdrop-blur-md dark:border-stone-700/70 dark:bg-stone-900/90'>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        className='h-7 px-2'
        onClick={() => {
          setTime(0);
          setPlaying(false);
        }}
        title={t('rainfallPlayback.reset') as string}>
        <RotateCcw className='size-3.5' />
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        className='h-7 px-2'
        onClick={() => {
          // If the user clicks Play at the end, rewind first so they
          // see something happen.
          if (!playing && time >= stormDurationS) setTime(0);
          setPlaying(!playing);
        }}
        title={
          (playing
            ? t('rainfallPlayback.pauseHint')
            : t('rainfallPlayback.playHint')) as string
        }>
        {playing ? <Pause className='size-3.5' /> : <Play className='size-3.5' />}
      </Button>
      {layerName && (
        <span
          className='hidden max-w-[120px] truncate text-[11px] text-slate-500 dark:text-stone-400 md:inline'
          title={layerName}>
          {layerName}
        </span>
      )}
      <div className='relative flex-1 cursor-grab active:cursor-grabbing'>
        <Slider
          value={[time]}
          min={0}
          max={stormDurationS}
          step={Math.max(1, stormDurationS / 1000)}
          onValueChange={([v]) => {
            setTime(v);
            // Manual scrub pauses — the user wants to inspect a moment.
            if (playing) setPlaying(false);
          }}
        />
        {/* Overflow markers — Feature 2. Each tick is positioned by
            `effectiveFillTimeS / stormDurationS`. We clamp anything that
            would land past the right edge to 100% and append a "→"
            badge that means "doesn't overflow during the storm" — the
            "→" reads more cleanly than omitting markers entirely
            because it preserves the at-a-glance ordering. Hover for a
            tooltip; click to jump the slider. */}
        {markers.length > 0 && (
          <div className='pointer-events-none absolute inset-x-0 top-0 h-full'>
            {markers.map((m) => (
              <button
                key={m.pondId}
                type='button'
                onClick={() => {
                  if (playing) setPlaying(false);
                  setTime(Math.min(stormDurationS, m.effectiveFillTimeS));
                }}
                title={
                  m.clamped
                    ? (t('rainfallPlayback.markerNoOverflow', {
                        pondLabel: m.pondId,
                      }) as string)
                    : (t('rainfallPlayback.markerTooltip', {
                        pondLabel: m.pondId,
                        time: fmtDurationS(m.effectiveFillTimeS),
                      }) as string)
                }
                aria-label={
                  m.clamped
                    ? (t('rainfallPlayback.markerNoOverflow', {
                        pondLabel: m.pondId,
                      }) as string)
                    : (t('rainfallPlayback.markerTooltip', {
                        pondLabel: m.pondId,
                        time: fmtDurationS(m.effectiveFillTimeS),
                      }) as string)
                }
                style={{ left: `${m.percent}%` }}
                className={`pointer-events-auto absolute -translate-x-1/2 ${
                  m.clamped ? 'top-[14px]' : '-top-[10px]'
                }`}>
                {m.clamped ? (
                  <span
                    className={`flex h-3 items-center px-0.5 text-[9px] font-medium leading-none ${markerColourText(m.tone)}`}>
                    →
                  </span>
                ) : (
                  <span
                    className={`block h-2 w-[3px] rounded-sm border border-white/80 dark:border-stone-900/80 ${markerColourBg(m.tone)}`}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <span className='whitespace-nowrap font-mono tabular-nums text-slate-700 dark:text-stone-300'>
        {fmtClock(time, stormDurationS)} / {fmtClock(stormDurationS, stormDurationS)}
        {intensityMmPerHr != null && Number.isFinite(intensityMmPerHr) && (
          <span className='ml-1.5 text-slate-500 dark:text-stone-400'>
            · {intensityMmPerHr.toFixed(0)} {t('rainfallPlayback.mmPerHr')}
          </span>
        )}
        {/* Scaled-intensity badge — only when the user moved the
            what-if slider away from 1×. Lets the audience read the
            scenario at a glance during a demo. */}
        {isScaled && scaledIntensity != null && (
          <span
            className='ml-1.5 italic text-sky-700 dark:text-sky-300'
            title={t('rainfallPlayback.scaledHint') as string}>
            × {stormScale.toFixed(2)} = {scaledIntensity.toFixed(0)}{' '}
            {t('rainfallPlayback.mmPerHr')}
          </span>
        )}
      </span>
      <div
        className='flex items-center gap-0.5 rounded border border-slate-200 bg-slate-50 p-0.5 dark:border-stone-700 dark:bg-stone-800'
        title={t('rainfallPlayback.speedTooltip') as string}>
        {SPEED_OPTIONS.map((s) => (
          <button
            key={s}
            type='button'
            onClick={() => setSpeed(s)}
            title={
              s === 1
                ? (t('rainfallPlayback.realtime') as string)
                : `${s}× ${t('rainfallPlayback.realtime')}`
            }
            className={`rounded px-1.5 py-0.5 text-[11px] tabular-nums transition ${
              speed === s
                ? 'bg-white font-medium text-slate-900 shadow-sm dark:bg-stone-700 dark:text-stone-100'
                : 'text-slate-500 hover:text-slate-900 dark:text-stone-400 dark:hover:text-stone-200'
            }`}>
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact MM:SS / HH:MM:SS clock for the timestamp readout. Switches
 * to h:mm:ss only when the storm duration warrants it (≥ 60 min) so
 * short demo storms read as "12:45" instead of "0:12:45".
 */
function fmtClock(s: number, totalDurationS = s): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (totalDurationS >= 3600 || h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Per-pond marker derived for the annotated playback timeline. */
interface PlaybackMarker {
  pondId: string;
  /** Effective fill time after applying the what-if scale (s). May
   *  exceed `stormDurationS`, in which case the marker is clamped to
   *  the slider's right edge with `clamped = true`. */
  effectiveFillTimeS: number;
  /** Position along the slider, 0..100. Always in [0, 100]. */
  percent: number;
  /** True iff the pond doesn't overflow within the storm window
   *  (effectiveFillTimeS > stormDurationS). Drives the "→" indicator. */
  clamped: boolean;
  /** Visual tone — matches the leaderboard status dot:
   *    rose: overflows during storm
   *    amber: clamped (past storm end) but partial-capture
   *    emerald: clamped + comfortably within capacity */
  tone: 'rose' | 'amber' | 'emerald';
}

/** Top-N most-imminent ponds. Anything past this gets dropped to keep
 *  the timeline readable on dense surveys. */
const MAX_MARKERS = 7;

/** Build the marker list for the playback timeline. */
function useMarkerEvents(
  features: ReadonlyArray<unknown> | undefined,
  gullies: Record<string, boolean>,
  stormScale: number,
  stormDurationS: number,
): PlaybackMarker[] {
  return useMemo(() => {
    if (!features?.length || !(stormDurationS > 0)) return [];
    const out: PlaybackMarker[] = [];
    for (const raw of features) {
      const f = raw as { properties?: Record<string, unknown> | null };
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.kind !== 'pond') continue;
      const pondId = props.pondId as string | undefined;
      if (!pondId) continue;
      // Drained ponds never fill; skip the marker outright.
      if (isPondDrained(props, gullies)) continue;
      const fillTimeRaw = props.fillTimeS;
      if (
        typeof fillTimeRaw !== 'number' ||
        !Number.isFinite(fillTimeRaw) ||
        fillTimeRaw <= 0
      ) {
        continue;
      }
      const eff = scaledFillTimeS(fillTimeRaw, stormScale);
      if (eff == null) continue;
      const totalRunoffM3 = props.totalRunoffM3 as number | undefined;
      const volumeM3 = props.volumeM3 as number | undefined;
      const safeScale = stormScale > 0 ? stormScale : 1;
      const scaledRunoff =
        typeof totalRunoffM3 === 'number' ? totalRunoffM3 * safeScale : undefined;
      const captureRatio =
        scaledRunoff != null && scaledRunoff > 0 && typeof volumeM3 === 'number'
          ? volumeM3 / scaledRunoff
          : null;
      const clamped = eff > stormDurationS;
      const percent = Math.min(100, Math.max(0, (eff / stormDurationS) * 100));
      // Tone follows the leaderboard:
      //   - overflows during storm → rose
      //   - clamped past storm-end + partial-capture → amber
      //   - clamped past storm-end + capacity OK → emerald
      let tone: PlaybackMarker['tone'] = 'rose';
      if (clamped) {
        tone = captureRatio != null && captureRatio < 1 ? 'amber' : 'emerald';
      }
      out.push({ pondId, effectiveFillTimeS: eff, percent, clamped, tone });
    }
    out.sort((a, b) => a.effectiveFillTimeS - b.effectiveFillTimeS);
    return out.slice(0, MAX_MARKERS);
  }, [features, gullies, stormScale, stormDurationS]);
}

const markerColourBg = (tone: PlaybackMarker['tone']) => {
  if (tone === 'rose') return 'bg-rose-500';
  if (tone === 'amber') return 'bg-amber-500';
  return 'bg-emerald-500';
};
const markerColourText = (tone: PlaybackMarker['tone']) => {
  if (tone === 'rose') return 'text-rose-600 dark:text-rose-300';
  if (tone === 'amber') return 'text-amber-600 dark:text-amber-300';
  return 'text-emerald-600 dark:text-emerald-300';
};

/**
 * Wrapper that mounts the playback bar only when at least one
 * rainfall layer is visible AND has STORM mode metadata. Encapsulates
 * the "should this UI exist" gate so the Viewer doesn't have to
 * inspect rainfall layer metadata itself.
 */
export function RainfallPlaybackBarMount({
  layers,
}: {
  layers: ReadonlyArray<{
    id?: string;
    name?: string | null;
    visible?: boolean;
    isHidden?: boolean;
    metadata?: unknown;
  }>;
}) {
  const setTime = useSetAtom(rainfallPlaybackTimeAtom);
  const setPlaying = useSetAtom(rainfallPlaybackPlayingAtom);

  const stormInfo = (() => {
    let firstStorm: {
      stormDurationS: number;
      intensityMmPerHr?: number;
      layerName?: string;
      layerId?: string;
    } | null = null;
    let stormCount = 0;
    for (const l of layers) {
      if (l.isHidden || l.visible === false) continue;
      const meta = (l.metadata ?? {}) as Record<string, unknown>;
      if (meta.type !== 'RAINFALL_VECTOR') continue;
      const summary = meta.rainfallSummary as
        | { mode?: string; storm?: { durationH?: number; intensityMmPerHr?: number } }
        | undefined;
      if (summary?.mode !== 'STORM') continue;
      const h = summary?.storm?.durationH;
      if (typeof h !== 'number' || h <= 0) continue;
      stormCount += 1;
      if (firstStorm == null) {
        firstStorm = {
          stormDurationS: h * 3600,
          intensityMmPerHr: summary?.storm?.intensityMmPerHr,
          layerName: l.name ?? undefined,
          layerId: l.id ?? undefined,
        };
      }
    }
    // Only surface the layer name when more than one STORM layer is
    // visible — the bar is global (single shared playback time atom),
    // so users with two STORM layers in view need to know which one
    // the timeline drives. Single-layer case stays clean.
    if (firstStorm && stormCount > 1) {
      return firstStorm;
    }
    if (firstStorm) {
      return { ...firstStorm, layerName: undefined };
    }
    return null;
  })();

  // When the playback bar disappears (no STORM layer visible), reset the
  // playback state so a future re-mount starts clean rather than at the
  // user's previous scrub position.
  const visible = stormInfo != null;
  const wasVisibleRef = useRef(visible);
  useEffect(() => {
    if (wasVisibleRef.current && !visible) {
      setTime(0);
      setPlaying(false);
    }
    wasVisibleRef.current = visible;
  }, [visible, setTime, setPlaying]);

  if (!stormInfo) return null;
  return (
    <RainfallPlaybackBar
      stormDurationS={stormInfo.stormDurationS}
      intensityMmPerHr={stormInfo.intensityMmPerHr}
      layerName={stormInfo.layerName}
      layerId={stormInfo.layerId}
    />
  );
}

